import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import { 
  BarChart3, 
  Info,
  Clock,
  TrendingUp,
  TrendingDown,
  BarChart,
} from 'lucide-react';
import styles from './MetricInsightModal.module.sass';
import { ProBadgeSmall, ProSection } from 'utils/ProBadge';

interface NavButtonProps {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
  isPro?: boolean;
}

const NavButton: React.FC<NavButtonProps> = ({ icon, label, active, onClick, isPro }) => (
  <button
    className={`${styles.navButton} ${active ? styles.active : ''}`}
    onClick={onClick}
  >
    {icon}
    {label}
    {isPro && <ProBadgeSmall />}
  </button>
);

interface MetricInsightModalProps {
  open: boolean;
  onClose: () => void;
  metricName: string;
  score: number;
  description: string;
  membershipStatus?: {
    max_wallets: number;
  } | null;
  onUpgrade?: () => void;
}

const MetricInsightModal: React.FC<MetricInsightModalProps> = ({
  open,
  onClose,
  metricName,
  score,
  description,
  membershipStatus = null,
  onUpgrade = () => {}
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  
  const getScoreColor = (score: number) => {
    if (score < 2) return '#f44336';  // Red
    if (score < 3.5) return '#9c27b0'; // Purple
    return '#73F6FF';  // Cyan
  };

  const getTrendIcon = (trend: string) => {
    if (trend === 'up') return <TrendingUp size={16} />;
    if (trend === 'down') return <TrendingDown size={16} />;
    return null;
  };

  const getTrendClass = (trend: string) => {
    if (trend === 'up') return styles.positive;
    if (trend === 'down') return styles.negative;
    return styles.neutral;
  };

  const getScoreImplications = (score: number) => {
    if (score >= 4) {
      return "This score suggests high market confidence and strong underlying fundamentals. Assets with these metrics typically experience more stable price action and sustained liquidity.";
    } else if (score >= 3) {
      return "This score indicates healthy market dynamics with adequate trading interest. Assets in this range typically maintain consistency during moderate market fluctuations.";
    } else if (score >= 2) {
      return "This score may signal increased volatility or reduced market depth. Such assets might experience larger price swings during broader market movements.";
    } else {
      return "This score often correlates with reduced trading volume and higher risk profiles. Assets in this range typically exhibit increased price sensitivity to external market factors.";
    }
  };

  // Sample historical data - in a real app, this would come from props or API
  const historicalData = [
    { date: '2024-02-10', score: 3.2, trend: 'up' },
    { date: '2024-02-17', score: 3.5, trend: 'up' },
    { date: '2024-02-24', score: 3.1, trend: 'down' },
    { date: '2024-03-01', score: 3.4, trend: 'up' },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <>
            <div className={styles.infoSection}>
              <h3>
                About {metricName} 
                {metricName === 'Liquidity Health' || metricName === 'Buy/Sell Pressure' || metricName === 'Price Stability' || metricName === 'Market Activity' ? 
                  <span className={styles.weightLabel}>
                    {metricName === 'Liquidity Health' ? 'Contributes 35% to overall score' : 
                     metricName === 'Buy/Sell Pressure' || metricName === 'Price Stability' ? 'Contributes 25% to overall score' : 
                     metricName === 'Market Activity' ? 'Contributes 15% to overall score' : ''}
                  </span> : null}
              </h3>
              <p>{description}</p>
            </div>
            
            <div className={styles.infoSection}>
              <h3>Current Assessment</h3>
              <div className={styles.scoreIndicator} style={{
                background: `linear-gradient(to right, ${getScoreColor(score)}22, ${getScoreColor(score)}11)`,
                borderLeft: `4px solid ${getScoreColor(score)}`
              }}>
                <div className={styles.scoreCategory}>
                  {score >= 4 ? 'EXCEPTIONAL' : 
                  score >= 3 ? 'FAVORABLE' : 
                  score >= 2 ? 'MODERATE' : 'CAUTIONARY'}
                </div>
                <div className={styles.scoreExplanation}>
                  {getScoreImplications(score)}
                </div>
              </div>
            </div>
            
            <div className={styles.infoSection}>
              <h3>Key Contributing Factors</h3>
              <p>These indicators contribute to the {metricName} assessment:</p>
              <div className={styles.factorsList}>
                {metricName === 'Buy/Sell Pressure' && (
                  <>
                    <div className={styles.factorItem}>
                      <TrendingUp size={16} /> Buy Order Strength
                    </div>
                    <div className={styles.factorItem}>
                      <TrendingDown size={16} /> Sell Order Volume
                    </div>
                    <div className={styles.factorItem}>
                      <BarChart size={16} /> Market Direction
                    </div>
                    <div className={styles.factorItem}>
                      <Clock size={16} /> 24-Hour Trend
                    </div>
                  </>
                )}
                
                {metricName === 'Liquidity Health' && (
                  <>
                    <div className={styles.factorItem}>
                      <BarChart size={16} /> Liquidity Depth
                    </div>
                    <div className={styles.factorItem}>
                      <Info size={16} /> Volume Distribution
                    </div>
                    <div className={styles.factorItem}>
                      <TrendingUp size={16} /> Market Resilience
                    </div>
                    <div className={styles.factorItem}>
                      <Clock size={16} /> Liquidity Consistency
                    </div>
                  </>
                )}
                
                {metricName === 'Price Stability' && (
                  <>
                    <div className={styles.factorItem}>
                      <TrendingUp size={16} /> Volatility Index
                    </div>
                    <div className={styles.factorItem}>
                      <BarChart size={16} /> Price Fluctuation
                    </div>
                    <div className={styles.factorItem}>
                      <Info size={16} /> Short-term Stability
                    </div>
                    <div className={styles.factorItem}>
                      <Clock size={16} /> Long-term Movement
                    </div>
                  </>
                )}
                
                {metricName === 'Market Activity' && (
                  <>
                    <div className={styles.factorItem}>
                      <BarChart size={16} /> Transaction Count
                    </div>
                    <div className={styles.factorItem}>
                      <Info size={16} /> Unique Participants
                    </div>
                    <div className={styles.factorItem}>
                      <TrendingUp size={16} /> Market Engagement
                    </div>
                    <div className={styles.factorItem}>
                      <Clock size={16} /> Activity Distribution
                    </div>
                  </>
                )}
                
                {metricName === 'Overall Health Score' && (
                  <>
                    <div className={styles.factorItem}>
                      <BarChart size={16} /> Liquidity Health (35%)
                    </div>
                    <div className={styles.factorItem}>
                      <TrendingUp size={16} /> Buy/Sell Pressure (25%)
                    </div>
                    <div className={styles.factorItem}>
                      <Info size={16} /> Price Stability (25%)
                    </div>
                    <div className={styles.factorItem}>
                      <Clock size={16} /> Market Activity (15%)
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        );
      case 'history':
        return (
          <ProSection membershipStatus={membershipStatus} onUpgrade={onUpgrade}>
            <div className={styles.infoSection}>
              <h3>Historical Performance</h3>
              <p>Track how this metric has changed over time to identify trends and anticipate future movements.</p>
              
              <div className={styles.chart}>
                <div className={styles.placeholder}>
                  Historical Chart Visualization
                </div>
              </div>
              
              <div style={{ marginTop: '20px' }}>
                <h3>Recent Readings</h3>
                {historicalData.map((item, index) => (
                  <div key={index} style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    padding: '10px 0',
                    borderBottom: '1px solid rgba(115, 246, 255, 0.1)'
                  }}>
                    <span>{item.date}</span>
                    <div className={getTrendClass(item.trend)} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>{item.score.toFixed(2)}</span>
                      {getTrendIcon(item.trend)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className={styles.infoSection}>
              <h3>Performance Analysis</h3>
              <p>
                This metric has shown a {
                  historicalData[historicalData.length-1].score > historicalData[0].score 
                    ? 'positive' 
                    : 'negative'
                } trend over the monitored period, with an average score of {
                  (historicalData.reduce((sum, item) => sum + item.score, 0) / historicalData.length).toFixed(2)
                }.
              </p>
              <p>
                Variations in this metric often precede significant market movements by 24-48 hours, 
                making it valuable for anticipating potential opportunities or risks.
              </p>
            </div>
          </ProSection>
        );
      default:
        return null;
    }
  };

  // MUI overrides for scrollbars
  const muiOverrideStyles = {
    paper: { overflow: 'hidden !important' },
    scrollPaper: { overflow: 'hidden !important' },
    container: { overflow: 'hidden !important' },
    root: { overflow: 'hidden !important' }
  };

  const implications = getScoreImplications(score);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      PaperProps={{
        className: styles.modalPaper,
        style: { overflow: 'hidden !important' }
      }}
      maxWidth={false}
      classes={{
        paper: muiOverrideStyles.paper,
        scrollPaper: muiOverrideStyles.scrollPaper,
        container: muiOverrideStyles.container,
        root: muiOverrideStyles.root
      }}
    >
      <div className={styles.container}>
        <div className={styles.sidebar}>
          <div className={styles.header}>
            <div className={styles.title}>{metricName}</div>
            <div className={styles.subtitle}>Detailed Analysis</div>
          </div>
          
          <div className={styles.nav}>
            <NavButton
              icon={<Info size={18} />}
              label="Overview"
              active={activeTab === 'overview'}
              onClick={() => setActiveTab('overview')}
            />
            <NavButton
              icon={<BarChart3 size={18} />}
              label="Historical Data"
              active={activeTab === 'history'}
              onClick={() => setActiveTab('history')}
              isPro={true}
            />
          </div>

          <div className={styles.scoreSection}>
            <div 
              className={styles.scoreCircle}
              style={{
                borderColor: score < 2 ? 'rgba(244, 67, 54, 0.3)' : 
                           score < 3.5 ? 'rgba(156, 39, 176, 0.3)' : 
                           'rgba(115, 246, 255, 0.3)'
              }}
            >
              <div className={styles.scoreNumber}>{score.toFixed(2)}</div>
              <div className={styles.scoreMax}>/5.00</div>
            </div>
            
            <div className={styles.scoreInfo}>
              <div className={styles.description}>
                {implications.split(' ').slice(0, 10).join(' ')}...
              </div>
            </div>
          </div>
        </div>

        <div className={styles.main}>
          <div className={styles.content}>
            {renderContent()}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default MetricInsightModal;